import React, { useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import './Lightbox.css';

function Lightbox({ photos, currentIndex, onClose, onNext, onPrev }) {
  const touchStart = useRef(null);
  const dialogRef = useRef(null);

  const handleKeyDown = useCallback((e) => {
    if (['Escape', 'ArrowRight', 'ArrowLeft'].includes(e.key)) e.preventDefault();
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowRight') onNext();
    if (e.key === 'ArrowLeft') onPrev();
    if (e.key === 'Tab') {
      const buttons = dialogRef.current.querySelectorAll('button');
      const first = buttons[0];
      const last = buttons[buttons.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }, [onClose, onNext, onPrev]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    const trigger = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    const root = document.getElementById('root');
    const previousInert = root?.inert;
    if (root) root.inert = true;
    document.body.style.overflow = 'hidden';
    dialogRef.current.querySelector('button').focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      if (root) root.inert = previousInert;
      if (trigger?.isConnected) trigger.focus({ preventScroll: true });
    };
  }, []);

  const handleTouchStart = (e) => {
    touchStart.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStart.current === null) return;
    const diff = touchStart.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) onNext();
      else onPrev();
    }
    touchStart.current = null;
  };

  const photo = photos[currentIndex];

  return createPortal(
    <AnimatePresence>
      <motion.div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Project photo gallery"
        className="lightbox"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        onClick={onClose}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <button className="lightbox-close" onClick={onClose} aria-label="Close">
          &times;
        </button>

        <button
          className="lightbox-arrow lightbox-prev"
          onClick={(e) => { e.stopPropagation(); onPrev(); }}
          aria-label="Previous photo"
        >
          &#8249;
        </button>

        <motion.div
          className="lightbox-image-wrapper"
          key={currentIndex}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.3 }}
          onClick={(e) => e.stopPropagation()}
        >
          <img src={photo.src} alt={photo.alt} />
        </motion.div>

        <button
          className="lightbox-arrow lightbox-next"
          onClick={(e) => { e.stopPropagation(); onNext(); }}
          aria-label="Next photo"
        >
          &#8250;
        </button>

        <div className="lightbox-counter" aria-live="polite">
          {currentIndex + 1} / {photos.length}
        </div>
      </motion.div>
    </AnimatePresence>, document.body
  );
}

export default Lightbox;
